import { Switch } from "@gettactic/components/src/primitives/Switch";
import { Plus, Trash2 } from "lucide-react";
import React from "react";
import { getUniqueId } from "../../../../../../../helpers";
import {
  WallDashPattern,
  WallDoor,
  WallLineCap,
  WallPathShape,
  WallWindow
} from "../../../../../types";
import { ColorPickerInput } from "../../../../ui/ColorPickerInput";
import { EditorFieldGrid, EditorFieldRow } from "../../../../ui/EditorField";
import { IconButton } from "../../../../ui/IconButton";
import { PanelSection } from "../../../../ui/PanelShell";
import { Seg } from "../../../../ui/Seg";
import {
  useMapEditorActions,
  useMapEditorUI
} from "../../../contexts/MapEditorContext";
import {
  DEFAULT_DOOR_WIDTH,
  DEFAULT_WALL_COLOR,
  DEFAULT_WINDOW_WIDTH
} from "../../../defines";
import { DragNumberInput } from "../../controls/ScrollDragNumberInput";
import { ShapeSectionOptions } from "../ShapePanel/ShapePanel";

const actionButtonClass =
  "flex h-7 items-center justify-center rounded-md border border-neutral-200 px-2 text-xs text-neutral-700 hover:bg-neutral-50";

export const WallPathPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape } = useMapEditorActions();
  const { wallPathSelectedVertexIndex } = useMapEditorUI();
  const shape = shapes[0] as WallPathShape;

  const handleThicknessChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        thickness: Number(value)
      } as Partial<WallPathShape>);
    });
  };

  const handleDashPatternChange = (value: WallDashPattern) => {
    shapes.forEach((s) => {
      updateShape(s.id, { dashPattern: value } as Partial<WallPathShape>);
    });
  };

  const handleLineCapStartChange = (value: WallLineCap) => {
    shapes.forEach((s) => {
      updateShape(s.id, { lineCapStart: value } as Partial<WallPathShape>);
    });
  };

  const handleLineCapEndChange = (value: WallLineCap) => {
    shapes.forEach((s) => {
      updateShape(s.id, { lineCapEnd: value } as Partial<WallPathShape>);
    });
  };

  const handleClosedToggle = () => {
    const newClosed = !shape.closed;
    const currentEdgeCount = shape.edges.length;
    const newEdgeCount = newClosed
      ? shape.vertices.length
      : shape.vertices.length - 1;

    let newEdges = [...shape.edges];
    if (newEdgeCount > currentEdgeCount) {
      newEdges.push({});
    } else if (newEdgeCount < currentEdgeCount) {
      newEdges = newEdges.slice(0, newEdgeCount);
    }

    updateShape(shape.id, {
      closed: newClosed,
      edges: newEdges
    } as Partial<WallPathShape>);
  };

  const handleFillToggle = () => {
    shapes.forEach((s) => {
      const wp = s as WallPathShape;
      updateShape(s.id, {
        // Default to the wall's own stroke color so fill+stroke read as the
        // same shape on first toggle. User can recolor afterwards.
        fillColor: wp.fillColor ? undefined : wp.color || DEFAULT_WALL_COLOR
      } as Partial<WallPathShape>);
    });
  };

  const handleFillColorChange = (value: string) => {
    shapes.forEach((s) => {
      updateShape(s.id, { fillColor: value } as Partial<WallPathShape>);
    });
  };

  const handleFillOpacityChange = (value: number | string) => {
    const opacity = Math.max(0, Math.min(100, Number(value))) / 100;
    shapes.forEach((s) => {
      updateShape(s.id, { fillOpacity: opacity } as Partial<WallPathShape>);
    });
  };

  // Per-vertex helpers
  const updateVertex = (
    vertexIndex: number,
    vertexUpdate: Record<string, unknown>
  ) => {
    const newVertices = [...shape.vertices];
    newVertices[vertexIndex] = { ...newVertices[vertexIndex], ...vertexUpdate };
    updateShape(shape.id, { vertices: newVertices } as Partial<WallPathShape>);
  };

  // Per-edge door/window helpers
  const updateEdge = (
    edgeIndex: number,
    edgeUpdate: Record<string, unknown>
  ) => {
    const newEdges = [...shape.edges];
    newEdges[edgeIndex] = { ...newEdges[edgeIndex], ...edgeUpdate };
    updateShape(shape.id, { edges: newEdges } as Partial<WallPathShape>);
  };

  const handleAddDoor = (edgeIndex: number) => {
    const edge = shape.edges[edgeIndex];
    const newDoor: WallDoor = {
      id: `door-${getUniqueId()}`,
      position: 0.5,
      width: DEFAULT_DOOR_WIDTH
    };
    updateEdge(edgeIndex, { doors: [...(edge.doors || []), newDoor] });
  };

  const handleRemoveDoor = (edgeIndex: number, doorId: string) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      doors: (edge.doors || []).filter((d) => d.id !== doorId)
    });
  };

  const handleDoorPositionChange = (
    edgeIndex: number,
    doorId: string,
    value: number | string
  ) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      doors: (edge.doors || []).map((d) =>
        d.id === doorId ? { ...d, position: Number(value) / 100 } : d
      )
    });
  };

  const handleDoorWidthChange = (
    edgeIndex: number,
    doorId: string,
    value: number | string
  ) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      doors: (edge.doors || []).map((d) =>
        d.id === doorId ? { ...d, width: Number(value) } : d
      )
    });
  };

  const handleDoorTransparentToggle = (edgeIndex: number, doorId: string) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      doors: (edge.doors || []).map((d) =>
        d.id === doorId ? { ...d, transparent: !d.transparent } : d
      )
    });
  };

  const handleAddWindow = (edgeIndex: number) => {
    const edge = shape.edges[edgeIndex];
    const newWindow: WallWindow = {
      id: `window-${getUniqueId()}`,
      position: 0.5,
      width: DEFAULT_WINDOW_WIDTH
    };
    updateEdge(edgeIndex, { windows: [...(edge.windows || []), newWindow] });
  };

  const handleRemoveWindow = (edgeIndex: number, windowId: string) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      windows: (edge.windows || []).filter((w) => w.id !== windowId)
    });
  };

  const handleWindowPositionChange = (
    edgeIndex: number,
    windowId: string,
    value: number | string
  ) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      windows: (edge.windows || []).map((w) =>
        w.id === windowId ? { ...w, position: Number(value) / 100 } : w
      )
    });
  };

  const handleWindowWidthChange = (
    edgeIndex: number,
    windowId: string,
    value: number | string
  ) => {
    const edge = shape.edges[edgeIndex];
    updateEdge(edgeIndex, {
      windows: (edge.windows || []).map((w) =>
        w.id === windowId ? { ...w, width: Number(value) } : w
      )
    });
  };

  return (
    <>
      <PanelSection label="Thickness">
        <div className="flex flex-col gap-1.5">
          <DragNumberInput
            size={"sm"}
            value={shape.thickness || 16}
            onChange={handleThicknessChange}
            min={1}
            max={100}
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => handleThicknessChange(16)}
              className={`${actionButtonClass} flex-1`}
            >
              Internal (16)
            </button>
            <button
              type="button"
              onClick={() => handleThicknessChange(32)}
              className={`${actionButtonClass} flex-1`}
            >
              External (32)
            </button>
          </div>
        </div>
      </PanelSection>

      <PanelSection label="Line Style">
        <Seg
          label="Line Style"
          value={shape.dashPattern || "solid"}
          onValueChange={handleDashPatternChange}
          options={[
            { value: "solid", label: "Solid" },
            { value: "dashed", label: "Dashed" },
            { value: "dotted", label: "Dotted" }
          ]}
        />
      </PanelSection>

      <PanelSection label="Caps">
        <EditorFieldGrid>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Start Cap</span>
            <Seg
              label="Start Cap"
              value={shape.lineCapStart || "butt"}
              onValueChange={handleLineCapStartChange}
              options={[
                { value: "butt", label: "Butt" },
                { value: "round", label: "Round" }
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">End Cap</span>
            <Seg
              label="End Cap"
              value={shape.lineCapEnd || "butt"}
              onValueChange={handleLineCapEndChange}
              options={[
                { value: "butt", label: "Butt" },
                { value: "round", label: "Round" }
              ]}
            />
          </div>
        </EditorFieldGrid>
      </PanelSection>

      <PanelSection label="Path">
        <EditorFieldRow label="Closed Path">
          <Switch
            checked={!!shape.closed}
            onCheckedChange={handleClosedToggle}
          />
        </EditorFieldRow>
        <p className="mt-2 text-neutral-500 text-xs">
          {shape.vertices.length} vertices, {shape.edges.length} edges
        </p>
      </PanelSection>

      {/* Fill is only meaningful when the path is closed; the toggle stays
       *  visible in either state but is disabled when not closed so it's
       *  obvious why it has no effect. */}
      <PanelSection label="Fill">
        <EditorFieldRow
          label="Fill"
          className={shape.closed ? undefined : "opacity-50"}
        >
          <Switch
            checked={!!shape.fillColor}
            onCheckedChange={handleFillToggle}
            disabled={!shape.closed}
          />
        </EditorFieldRow>
        {shape.closed && shape.fillColor && (
          <div className="mt-2 flex flex-col gap-2">
            <EditorFieldRow label="Color">
              <ColorPickerInput
                value={shape.fillColor}
                onChange={handleFillColorChange}
              />
            </EditorFieldRow>
            <EditorFieldRow label="Opacity (%)">
              <DragNumberInput
                size={"sm"}
                className="w-20"
                value={Math.round((shape.fillOpacity ?? 1) * 100)}
                onChange={handleFillOpacityChange}
                min={0}
                max={100}
              />
            </EditorFieldRow>
          </div>
        )}
      </PanelSection>

      {/* Corner radius for selected vertex */}
      {wallPathSelectedVertexIndex !== null &&
        wallPathSelectedVertexIndex < shape.vertices.length && (
          <PanelSection label={`Vertex ${wallPathSelectedVertexIndex + 1}`}>
            <EditorFieldRow label="Corner Radius">
              <DragNumberInput
                size={"sm"}
                className="w-20"
                value={
                  shape.vertices[wallPathSelectedVertexIndex].cornerRadius || 0
                }
                onChange={(v) =>
                  updateVertex(wallPathSelectedVertexIndex, {
                    cornerRadius: Number(v)
                  })
                }
                min={0}
                max={200}
              />
            </EditorFieldRow>
          </PanelSection>
        )}

      {/* Per-edge controls */}
      {shape.edges.map((edge, edgeIdx) => (
        <PanelSection key={edgeIdx} label={`Edge ${edgeIdx + 1}`}>
          <div className="flex flex-col gap-2">
            <EditorFieldRow label="Doors">
              <button
                type="button"
                onClick={() => handleAddDoor(edgeIdx)}
                className={`${actionButtonClass} gap-1`}
              >
                <Plus size={10} />
                Add
              </button>
            </EditorFieldRow>
            {(edge.doors || []).map((door, di) => (
              <div
                key={door.id}
                className="flex flex-col gap-1 rounded-md border border-neutral-100 p-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600 text-xs">
                    Door {di + 1}
                  </span>
                  <IconButton
                    label="Remove door"
                    size="sm"
                    isDestructive
                    onClick={() => handleRemoveDoor(edgeIdx, door.id)}
                  >
                    <Trash2 size={12} />
                  </IconButton>
                </div>
                <EditorFieldGrid>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-neutral-600 text-xs">Pos (%)</span>
                    <DragNumberInput
                      size={"sm"}
                      value={Math.round(door.position * 100)}
                      onChange={(v) =>
                        handleDoorPositionChange(edgeIdx, door.id, v)
                      }
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-neutral-600 text-xs">Width</span>
                    <DragNumberInput
                      size={"sm"}
                      value={door.width}
                      onChange={(v) =>
                        handleDoorWidthChange(edgeIdx, door.id, v)
                      }
                      min={5}
                      max={200}
                    />
                  </div>
                </EditorFieldGrid>
                <label className="flex cursor-pointer items-center gap-2 text-neutral-600 text-xs">
                  <input
                    type="checkbox"
                    checked={!!door.transparent}
                    onChange={() =>
                      handleDoorTransparentToggle(edgeIdx, door.id)
                    }
                    className="rounded"
                  />
                  Transparent
                </label>
              </div>
            ))}

            <EditorFieldRow label="Windows">
              <button
                type="button"
                onClick={() => handleAddWindow(edgeIdx)}
                className={`${actionButtonClass} gap-1`}
              >
                <Plus size={10} />
                Add
              </button>
            </EditorFieldRow>
            {(edge.windows || []).map((win, wi) => (
              <div
                key={win.id}
                className="flex flex-col gap-1 rounded-md border border-neutral-100 p-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600 text-xs">
                    Window {wi + 1}
                  </span>
                  <IconButton
                    label="Remove window"
                    size="sm"
                    isDestructive
                    onClick={() => handleRemoveWindow(edgeIdx, win.id)}
                  >
                    <Trash2 size={12} />
                  </IconButton>
                </div>
                <EditorFieldGrid>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-neutral-600 text-xs">Pos (%)</span>
                    <DragNumberInput
                      size={"sm"}
                      value={Math.round(win.position * 100)}
                      onChange={(v) =>
                        handleWindowPositionChange(edgeIdx, win.id, v)
                      }
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-neutral-600 text-xs">Width</span>
                    <DragNumberInput
                      size={"sm"}
                      value={win.width}
                      onChange={(v) =>
                        handleWindowWidthChange(edgeIdx, win.id, v)
                      }
                      min={5}
                      max={200}
                    />
                  </div>
                </EditorFieldGrid>
              </div>
            ))}
          </div>
        </PanelSection>
      ))}
    </>
  );
};
